/**
 * Firebase Authentication Error Handling Utility
 *
 * Categorizes Firebase auth errors into actionable vs generic messages.
 * Follows SCIO1 patterns where appropriate, improves where needed.
 *
 * Categories:
 * 1. Actionable - User can fix (wrong password, expired code, etc.)
 * 2. Informational - User should know but can't fix directly (account disabled)
 * 3. Generic - Internal/config issues, show "Something went wrong"
 */

type TranslateFunction = (key: string) => string;

/**
 * Context-aware error messages for different authentication flows.
 */
export type FirebaseErrorContext =
  | 'login' // Email/password login, social login
  | 'mfa' // MFA verification (TOTP, SMS)
  | 'password-change' // Change password dialog
  | 'password-reset' // Password reset from email link
  | 'enrollment' // MFA enrollment (TOTP, Phone)
  | 'magic-link' // Magic link sign-in
  | 'reauth'; // Reauthentication dialog

/**
 * Maps Firebase Auth error codes to user-friendly messages.
 *
 * @param code - Firebase error code (e.g., 'auth/wrong-password')
 * @param t - i18next translation function
 * @param context - Authentication flow context for tailored messages
 * @returns User-friendly error message
 */
export function getFirebaseAuthErrorMessage(
  code: string | undefined,
  t: TranslateFunction,
  context: FirebaseErrorContext = 'login',
): string {
  // Normalize code (remove 'auth/' prefix if checking without it)
  const normalizedCode = code ?? '';

  switch (normalizedCode) {
    // ============================================================
    // LOGIN CREDENTIAL ERRORS (Actionable)
    // ============================================================
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
    case 'auth/invalid-login-credentials':
      // Context-aware: login vs password change/reauth
      if (context === 'login') {
        return t('Incorrect email or password');
      }
      return t('Current password is incorrect');

    case 'auth/invalid-email':
      return t('Please enter a valid email address');

    case 'auth/user-not-found':
      // Log in: the SAME message as a wrong password, so the page never reveals whether an
      // address has an account (account enumeration, #2291). Prod's Firebase already returns one
      // code for both; projects with Email Enumeration Protection off (qa3d) return this one.
      // Other contexts (reauth, settings) are for a signed-in user, where it reveals nothing.
      if (context === 'login') {
        return t('Incorrect email or password');
      }
      return t('No account found with this email address.');

    case 'auth/email-already-in-use':
      return t('This email is already registered');

    // ============================================================
    // ACCOUNT STATUS ERRORS (Informational)
    // ============================================================
    case 'auth/user-disabled':
      return t('This account has been disabled.');

    case 'auth/operation-not-allowed':
      return t('This sign-in method is not available');

    // ============================================================
    // PASSWORD ERRORS (Actionable)
    // ============================================================
    case 'auth/weak-password':
      return t('Password is too weak. Please choose a stronger password.');

    // ============================================================
    // RATE LIMITING (Actionable - wait)
    // ============================================================
    case 'auth/too-many-requests':
      return t('Too many attempts. Please try again later.');

    // ============================================================
    // MFA VERIFICATION ERRORS (Actionable)
    // ============================================================
    case 'auth/invalid-verification-code':
      return t('Code is invalid.');

    case 'auth/code-expired':
    case 'auth/totp-challenge-timeout':
      return t('Code is expired.');

    case 'auth/missing-verification-code':
      return t('Please enter the verification code.');

    case 'auth/invalid-verification-id':
    case 'auth/missing-verification-id':
      return t('Verification session expired. Please try again.');

    // ============================================================
    // MFA ENROLLMENT ERRORS (Actionable/Informational)
    // ============================================================
    case 'auth/invalid-mfa-enrollment-id':
    case 'auth/multi-factor-info-not-found':
      return t('Your 2FA enrollment is invalid. Please set up 2FA again.');

    case 'auth/second-factor-already-in-use':
      return t('This phone number is already registered for 2FA.');

    case 'auth/maximum-second-factor-count-exceeded':
      return t('Maximum number of 2FA methods reached.');

    case 'auth/too-many-enrollment-attempts':
      return t('Too many enrollment attempts. Please try again later.');

    case 'auth/unsupported-first-factor':
      return t(
        '2FA is not supported with your current sign-in method. Please sign in with email and password.',
      );

    case 'auth/unverified-email':
      return t('Please verify your email before enabling 2FA.');

    // ============================================================
    // SESSION/REAUTH ERRORS (Actionable)
    // ============================================================
    case 'auth/requires-recent-login':
    case 'auth/credential-too-old-login-again':
      return t('Please sign in again to continue.');

    case 'auth/user-token-expired':
    case 'auth/invalid-user-token':
      return t('Your session is invalid.');

    // ============================================================
    // ACTION CODE ERRORS - Password Reset, Email Links (Actionable)
    // ============================================================
    case 'auth/expired-action-code':
      if (context === 'magic-link') {
        return t('This sign-in link has expired. Please request a new one.');
      }
      return t('This link has expired. Please request a new one.');

    case 'auth/invalid-action-code':
      if (context === 'magic-link') {
        return t('This sign-in link has expired or already been used. Please request a new one.');
      }
      return t('This link is invalid or has been used. Please request a new one.');

    // ============================================================
    // PHONE NUMBER ERRORS (Actionable)
    // ============================================================
    case 'auth/invalid-phone-number':
      return t('Please enter a valid phone number');

    case 'auth/missing-phone-number':
      return t('Phone Number is required');

    // ============================================================
    // NETWORK ERRORS (Actionable)
    // ============================================================
    case 'auth/network-request-failed':
      return t('Network error. Please check your internet connection.');

    case 'auth/timeout':
      return t('Request timed out. Please try again.');

    // ============================================================
    // OAUTH/POPUP ERRORS (Actionable)
    // ============================================================
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
      return t('Sign-in was cancelled. Please try again.');

    case 'auth/popup-blocked':
      return t('Pop-up was blocked. Please allow pop-ups and try again.');

    case 'auth/account-exists-with-different-credential':
      return t(
        'An account already exists with the same email. Please sign in with your original method and link this provider from settings.',
      );

    case 'auth/credential-already-in-use':
      return t('This credential is already associated with another account.');

    // ============================================================
    // RECAPTCHA ERRORS (Actionable - retry)
    // ============================================================
    case 'auth/captcha-check-failed':
    case 'auth/invalid-app-credential':
    case 'auth/missing-app-credential':
      return t('Verification failed. Please try again.');

    case 'auth/recaptcha-not-enabled':
    case 'auth/missing-recaptcha-token':
    case 'auth/invalid-recaptcha-token':
      return t('Security verification failed. Please refresh and try again.');

    // ============================================================
    // GENERIC ERRORS (Internal/Config - no useful info for user)
    // These are issues users cannot fix themselves
    // ============================================================
    case 'auth/internal-error':
    case 'auth/invalid-api-key':
    case 'auth/app-not-authorized':
    case 'auth/invalid-app-id':
    case 'auth/argument-error':
    case 'auth/invalid-tenant-id':
    case 'auth/tenant-id-mismatch':
    case 'auth/cors-unsupported':
    case 'auth/web-storage-unsupported':
    case 'auth/invalid-persistence-type':
    case 'auth/unsupported-persistence-type':
    case 'auth/invalid-multi-factor-session':
    case 'auth/missing-multi-factor-session':
    case 'auth/missing-multi-factor-info':
    case 'auth/app-deleted':
    case 'auth/emulator-config-failed':
    case 'auth/dependent-sdk-initialized-before-auth':
    case 'auth/already-initialized':
      return t('Something went wrong! Please try again.');

    // ============================================================
    // DEFAULT FALLBACK (Generic)
    // Unknown errors - don't expose raw Firebase messages
    // ============================================================
    default:
      return t('Something went wrong! Please try again.');
  }
}

/**
 * Checks if a Firebase error code indicates the user should request a new link.
 * Useful for showing "Request new link" UI in password reset / magic link flows.
 */
export function isLinkExpiredOrInvalidError(code: string | undefined): boolean {
  return code === 'auth/expired-action-code' || code === 'auth/invalid-action-code';
}

/**
 * Checks if a Firebase error code indicates rate limiting.
 */
export function isRateLimitError(code: string | undefined): boolean {
  return code === 'auth/too-many-requests' || code === 'auth/too-many-enrollment-attempts';
}

/**
 * Checks if a Firebase error code indicates a network issue.
 */
export function isNetworkError(code: string | undefined): boolean {
  return code === 'auth/network-request-failed' || code === 'auth/timeout';
}

/**
 * Checks if a Firebase error code indicates session needs refresh.
 */
export function isSessionExpiredError(code: string | undefined): boolean {
  return (
    code === 'auth/requires-recent-login' ||
    code === 'auth/credential-too-old-login-again' ||
    code === 'auth/user-token-expired' ||
    code === 'auth/invalid-user-token'
  );
}

/**
 * Codes that mean a deploy broke auth for EVERYONE (near-zero baseline).
 * MUST be declared before SYSTEMIC_AUTH_CODES — `const` is not hoisted (TDZ),
 * so spreading it from above would throw at module load.
 * Keep in sync with §4 of the design doc and the Loki rules.
 */
const CONFIG_REGRESSION_CODES = new Set([
  // Thrown at getAuth() when the key is EMPTY/absent. A *wrong* key does NOT
  // produce this — see API_KEY_INVALID_CODE below.
  'auth/invalid-api-key',
  'auth/operation-not-allowed',
  'auth/configuration-not-found',
  'auth/unauthorized-domain',
  'auth/app-not-authorized',
  'auth/invalid-app-id',
  // The FirebaseApp instance itself is gone — nothing works for anyone.
  'auth/app-deleted',
  // Tenant misconfiguration — same "broken for everyone" shape as the above.
  'auth/invalid-tenant-id',
]);

/**
 * What the modular SDK (v9+, we are on firebase 12.x) actually emits for a
 * WRONG API key. It slugifies Google's English REST message into the code:
 *
 *   auth/api-key-not-valid.-please-pass-a-valid-api-key.
 *
 * VERIFIED EMPIRICALLY 2026-08-05 against firebase 12.8.0 by signing in with
 * `apiKey: 'broken-on-purpose'`. The tidy `auth/invalid-api-key` in the set above
 * is the **v8 namespaced SDK** code and is NOT emitted on this path — relying on
 * it alone meant a bad API key in production reported nothing at all.
 *
 * Matched by prefix because the trailing text is an untranslated server message
 * and could change wording; the `api-key-not-valid` stem is the stable part.
 */
const API_KEY_INVALID_CODE_PREFIX = 'auth/api-key-not-valid';

/** True for the wrong-API-key code whose exact suffix we do not want to depend on. */
function isApiKeyInvalidCode(code: string): boolean {
  return code.startsWith(API_KEY_INVALID_CODE_PREFIX);
}

/**
 * Firebase's backend 503 slugified into a code, same shape as the api-key one above:
 *
 *   auth/the-service-is-currently-unavailable.
 *
 * OBSERVED in prod (2026-08, `token-refresh` flow) landing as `unclassified`. It is a
 * transient FIREBASE-side outage, not the user's own connectivity (that is
 * `network-request-failed`, in CLIENT_NETWORK_AUTH_CODES) and not our config
 * (CONFIG_REGRESSION pages at sev1). It belongs with `auth/internal-error` /
 * `auth/quota-exceeded` in SYSTEMIC_AUTH_CODES — "the service is failing" — reported at
 * observation tier, never paging on one browser (the rule aggregates by host).
 *
 * Prefix-matched because the trailing text is an untranslated server message; the
 * `the-service-is-currently-unavailable` stem is the stable part.
 */
const SERVICE_UNAVAILABLE_CODE_PREFIX = 'auth/the-service-is-currently-unavailable';

/** True for the Firebase 503 code whose exact suffix we do not want to depend on. */
function isServiceUnavailableCode(code: string): boolean {
  return code.startsWith(SERVICE_UNAVAILABLE_CODE_PREFIX);
}

/**
 * Codes that are deliberately expected/never reported — normal per-user churn,
 * not a systemic signal. Do NOT "helpfully" add these to SYSTEMIC_AUTH_CODES or
 * CONFIG_REGRESSION_CODES:
 * - `auth/invalid-user-token`, `auth/user-token-expired` — session expiry
 * - `auth/web-storage-unsupported` — browser/privacy-mode limitation, not us
 * - `auth/requires-recent-login` — reauth flow, expected
 * - `auth/user-disabled` — admin action on one account
 * - `auth/wrong-password` — user typo
 * - `auth/invalid-credential` — CLIENT-SIDE meaning: wrong password/credential
 *   for one user. **Genuine footgun:** the Admin SDK emits the SAME code string
 *   `auth/invalid-credential` server-side to mean the service-account credential
 *   itself is broken (see `optisigns-open-api/src/app/auth/verify-failure.classifier.ts`
 *   → `admin_credential_invalid`) — a total outage, the opposite of this client
 *   meaning. Never conflate the two when reading logs/alerts across client vs server.
 * - `auth/user-not-found` — user typo / doesn't exist
 * - `auth/invalid-email` — user typo
 * - `auth/email-already-in-use` — expected signup collision
 * - `auth/weak-password` — user chose a weak password
 * - `auth/popup-closed-by-user` — user cancelled
 * - `auth/expired-action-code`, `auth/invalid-action-code` — stale/reused link
 */

/**
 * Codes whose cause is almost always the USER'S OWN connectivity, not our service:
 * a sleeping laptop, dropped Wi-Fi, a captive portal, an ad-blocker.
 *
 * These were in SYSTEMIC_AUTH_CODES until prod data settled it: over the first 4 days
 * of live telemetry `auth/network-request-failed` was 59 of 78 events (76%), and 54 of
 * those were the background `token-refresh` flow. Classifying one user's Wi-Fi blip as
 * "our project is failing many users at once" both contradicts SYSTEMIC_AUTH_CODES' own
 * contract and buries the real signal — the Google SRE anti-pattern of alerting on a
 * cause that is not a symptom of our service failing.
 *
 * Still REPORTED (as `client_network`), never dropped: a genuine outage on our side —
 * or a regional ISP/CDN event worth knowing about — shows up as this code firing across
 * MANY DISTINCT `host` values at once. That aggregation belongs in the alert rule, which
 * can group by host; it cannot be decided from a single browser's error.
 */
const CLIENT_NETWORK_AUTH_CODES = new Set(['auth/network-request-failed', 'auth/timeout']);

/** Codes meaning OUR project/config/quota is failing many users at once. */
const SYSTEMIC_AUTH_CODES = new Set([
  'auth/too-many-requests',
  'auth/internal-error',
  'auth/quota-exceeded',
  // Our code is calling Firebase incorrectly — broken for everyone in principle,
  // but some flows can produce it from odd input, so it must stay OUT of
  // CONFIG_REGRESSION_CODES (which pages at sev1).
  'auth/argument-error',
  ...CONFIG_REGRESSION_CODES,
]);

/**
 * Codes that are NORMAL user error. Never reported, and — just as important —
 * they are what lets us tell "expected" apart from "we have never seen this
 * code before". Anything in neither this set nor the systemic sets is
 * `unclassified` and gets reported at low severity, so a code the SDK emits
 * that our sets do not know about surfaces as a ticket instead of silence.
 *
 * This list was previously a prose comment. It is a real set now precisely
 * because the prose version could not be enforced or queried.
 */
const EXPECTED_AUTH_CODES = new Set([
  // credential / identity mistakes
  'auth/wrong-password',
  'auth/invalid-credential', // CLIENT meaning: wrong password. NOT the Admin-SDK meaning.
  'auth/invalid-login-credentials',
  'auth/user-not-found',
  'auth/invalid-email',
  'auth/email-already-in-use',
  'auth/weak-password',
  // session lifecycle — the user simply needs to sign in again
  'auth/user-token-expired',
  'auth/invalid-user-token',
  'auth/requires-recent-login',
  'auth/credential-too-old-login-again',
  // Reauth against a DIFFERENT account than the one signed in — the user picked the
  // wrong account. Expected user error, observed in prod landing as `unclassified`.
  'auth/user-mismatch',
  'auth/user-disabled',
  // user-cancelled / browser-environment
  'auth/popup-closed-by-user',
  'auth/cancelled-popup-request',
  // The user dismissed a native/redirect sign-in flow. Sibling of the two above;
  // observed in prod and missing here, so it was reported as `unclassified`.
  'auth/user-cancelled',
  'auth/popup-blocked',
  'auth/web-storage-unsupported',
  // action codes (reset / verify / magic link)
  'auth/expired-action-code',
  'auth/invalid-action-code',
  // MFA challenge churn
  //
  // NOT an error: Firebase THROWS this code as the normal way to signal "this user has
  // a second factor, now resolve it" — every successful MFA sign-in raises it. Missing
  // here, it was reported as `unclassified` on each MFA login (9 events in the first 4
  // prod days), which would eventually trip ClientAuthUnclassifiedCode (>3 per code+flow
  // per 30m) on users signing in SUCCESSFULLY. MfaPage handles it as control flow.
  'auth/multi-factor-auth-required',
  'auth/invalid-verification-code',
  'auth/missing-verification-code',
  'auth/code-expired',
  'auth/totp-challenge-timeout',
  'auth/invalid-verification-id',
  'auth/missing-verification-id',
  'auth/second-factor-already-in-use',
  'auth/maximum-second-factor-count-exceeded',
  'auth/too-many-enrollment-attempts',
  'auth/unverified-email',
  'auth/multi-factor-info-not-found',
  'auth/invalid-mfa-enrollment-id',
  'auth/unsupported-first-factor',
  // linking
  'auth/account-exists-with-different-credential',
  'auth/credential-already-in-use',
  // phone input
  'auth/invalid-phone-number',
  'auth/missing-phone-number',
  // reCAPTCHA — user-retryable
  'auth/captcha-check-failed',
  'auth/missing-recaptcha-token',
  'auth/invalid-recaptcha-token',
  'auth/invalid-app-credential',
  'auth/missing-app-credential',
]);

/** True for codes we deliberately never report. */
export function isExpectedAuthError(code: string | undefined): boolean {
  return EXPECTED_AUTH_CODES.has(normalizeAuthCode(code));
}

/**
 * True when a code is in NEITHER the expected set nor any systemic set — i.e.
 * the SDK emitted something our taxonomy does not cover. Callers report these
 * at low severity so the gap is visible.
 */
export function isUnclassifiedAuthError(code: string | undefined): boolean {
  const normalized = normalizeAuthCode(code);
  if (!normalized) return false;
  return (
    !isExpectedAuthError(normalized) &&
    !isSystemicAuthError(normalized) &&
    // Classified, just not as ours. Without this clause, moving these codes out of
    // SYSTEMIC_AUTH_CODES would silently reclassify them as `unclassified` — which is
    // WORSE than the bug being fixed, because `unclassified` has its own alert.
    !isClientNetworkAuthError(normalized)
  );
}

/**
 * The JS SDK emits `auth/user-not-found`; FlutterFire emits `user-not-found`.
 * Normalise to the prefixed form so one classifier serves both.
 */
export function normalizeAuthCode(code: string | undefined): string {
  if (!code) return '';
  return code.startsWith('auth/') ? code : `auth/${code}`;
}

/**
 * True when the error means the SERVICE is failing, not that one user mistyped.
 * Anything unrecognised returns false — fail safe, never alert on the unknown.
 */
export function isSystemicAuthError(code: string | undefined): boolean {
  const normalized = normalizeAuthCode(code);
  return (
    SYSTEMIC_AUTH_CODES.has(normalized) ||
    isApiKeyInvalidCode(normalized) ||
    isServiceUnavailableCode(normalized)
  );
}

/**
 * True when the failure is most likely the user's own network rather than our service.
 * Reported at its own severity so systemic rules stay clean — see CLIENT_NETWORK_AUTH_CODES.
 */
export function isClientNetworkAuthError(code: string | undefined): boolean {
  return CLIENT_NETWORK_AUTH_CODES.has(normalizeAuthCode(code));
}

/** True for the "a deploy locked everyone out" subset. Pages on first occurrence. */
export function isAuthConfigRegression(code: string | undefined): boolean {
  const normalized = normalizeAuthCode(code);
  return CONFIG_REGRESSION_CODES.has(normalized) || isApiKeyInvalidCode(normalized);
}
